(() => {
  'use strict';

  const parseBoolean = (value) => value === 'true';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let viewerScriptPromise;

  /* model-viewer is ~935 KB, so it is only fetched when a visitor actually asks
     for the 3D view. The drawn clock carries the hero until then. */
  const loadModelViewer = (src) => {
    if (viewerScriptPromise) return viewerScriptPromise;
    viewerScriptPromise = new Promise((resolve, reject) => {
      if (customElements.get('model-viewer')) { resolve(); return; }
      const script = document.createElement('script');
      script.type = 'module';
      script.src = src;
      script.onerror = () => reject(new Error('3D görüntüleyici yüklenemedi'));
      document.head.append(script);
      const timeout = window.setTimeout(() => reject(new Error('3D görüntüleyici zaman aşımına uğradı')), 20000);
      customElements.whenDefined('model-viewer').then(() => {
        window.clearTimeout(timeout);
        resolve();
      }, reject);
    });
    return viewerScriptPromise;
  };

  class MetaseryHero3d extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.stage = this.querySelector('[data-orv3d-stage]');
      this.modelSlot = this.querySelector('[data-orv3d-model-slot]');
      this.templates = [...this.querySelectorAll('[data-orv3d-model-template]')];
      this.loadButton = this.querySelector('[data-orv3d-load]');
      this.status = this.querySelector('[data-orv3d-status]');
      this.gesture = this.querySelector('[data-orv3d-gesture]');
      this.ledButton = this.querySelector('[data-orv3d-led]');
      this.ledStates = [...this.querySelectorAll('[data-orv3d-led-state]')];
      this.carousel = this.querySelector('[data-orv3d-carousel]');
      this.dots = [...this.querySelectorAll('[data-orv3d-dot]')];
      this.modelName = this.querySelector('[data-orv3d-model-name]');
      this.counter = this.querySelector('[data-orv3d-counter]');
      this.hasModel = parseBoolean(this.dataset.hasModel);
      this.index = 0;
      this.loading = false;

      this.startClock();

      this.ledButton?.addEventListener('click', () => this.toggleLed());
      this.loadButton?.addEventListener('click', () => this.loadModel(true));
      this.loadButton?.addEventListener('pointerenter', () => this.prefetchModel(), { once: true });

      this.querySelector('[data-orv3d-prev]')?.addEventListener('click', () => this.go(-1));
      this.querySelector('[data-orv3d-next]')?.addEventListener('click', () => this.go(1));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.select(Number(dot.dataset.orv3dDot)));
      });

      if (!this.hasModel) return;

      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const autoLoad = mobile ? parseBoolean(this.dataset.autoLoadMobile) : parseBoolean(this.dataset.autoLoadDesktop);
      if (!autoLoad) return;

      /* Hareket azaltma modeli ENGELLEMEZ: model durgun bir nesne, kisitlanmasi
         gereken sey salinim ve otomatik donus — ikisi de mountModel icinde zaten
         reducedMotion() ile kapaniyor. */
      if (!('IntersectionObserver' in window)) { this.loadModel(false); return; }

      this.observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        /* Bosta bekleme yok. Sahibi "cizilen saat yerine model" dedigi icin cizilen
           saat burada poster gorevi goruyor; model iner inmez yerine geciyor. */
        this.loadModel(false);
      }, { rootMargin: '180px 0px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.tick) window.clearInterval(this.tick);
    }

    /* --- drawn clock ------------------------------------------------------ */

    startClock() {
      if (!this.querySelector('[data-orv3d-clock]')) return;
      const paint = () => {
        const now = new Date();
        const seconds = now.getSeconds();
        const minutes = now.getMinutes() + seconds / 60;
        const hours = (now.getHours() % 12) + minutes / 60;
        this.style.setProperty('--orv-hour', `${(hours * 30).toFixed(2)}deg`);
        this.style.setProperty('--orv-min', `${(minutes * 6).toFixed(2)}deg`);
        this.style.setProperty('--orv-sec', `${(seconds * 6).toFixed(2)}deg`);
      };
      paint();
      // One timer, three custom properties: no layout is read or written per tick.
      this.tick = window.setInterval(paint, 1000);
    }

    /* --- backlight -------------------------------------------------------- */

    toggleLed() {
      const active = !this.classList.contains('is-led-on');
      this.classList.toggle('is-led-on', active);
      this.ledButton?.setAttribute('aria-checked', String(active));
      const label = active
        ? (this.ledButton?.dataset.onLabel || 'Açık')
        : (this.ledButton?.dataset.offLabel || 'Kapalı');
      this.ledStates.forEach((node) => { node.textContent = label; });
      const viewer = this.modelSlot?.querySelector('model-viewer');
      if (viewer) viewer.setAttribute('exposure', active ? '1.15' : '.85');
    }

    /* --- 3D model --------------------------------------------------------- */

    modelSources() {
      if (this.templates.length) return this.templates.map((template) => ({ template }));
      const url = this.validModelUrl();
      return url ? [{ url }] : [];
    }

    validModelUrl() {
      const value = (this.dataset.modelUrl || '').trim();
      if (!value) return '';
      try {
        const url = new URL(value, window.location.href);
        // Uretimde her sey https. Ayni origin"e de izin veriyoruz, aksi halde
        // "shopify theme dev" (http://127.0.0.1:9292) modeli hic gosteremiyor.
        if (url.protocol === 'https:') return url.href;
        if (url.origin === window.location.origin) return url.href;
        return '';
      } catch { return ''; }
    }

    prefetchModel() {
      const source = this.modelSources()[this.index];
      const url = source?.url || source?.template?.content.querySelector('model-viewer')?.getAttribute('src');
      if (!url || this.prefetched) return;
      this.prefetched = true;
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'fetch';
      link.href = url;
      link.crossOrigin = 'anonymous';
      document.head.append(link);
    }

    go(direction) {
      const total = this.modelSources().length;
      if (total < 2) return;
      this.select((this.index + direction + total) % total);
    }

    select(index) {
      const sources = this.modelSources();
      if (!sources.length) return;
      this.index = Math.max(0, Math.min(index, sources.length - 1));
      this.syncCarousel();
      if (this.classList.contains('is-model-ready')) this.mountModel();
    }

    syncCarousel() {
      const sources = this.modelSources();
      const pad = (value) => String(value).padStart(2, '0');
      this.dots.forEach((dot, position) => {
        dot.setAttribute('aria-current', String(position === this.index));
      });
      if (this.counter) this.counter.textContent = `${pad(this.index + 1)} / ${pad(sources.length)}`;
      if (this.modelName) {
        this.modelName.textContent = this.templates[this.index]?.dataset.orv3dModelName || '';
      }
    }

    mountModel() {
      const source = this.modelSources()[this.index];
      if (!source || !this.modelSlot) return null;
      this.modelSlot.replaceChildren();
      if (source.template) {
        this.modelSlot.append(source.template.content.cloneNode(true));
      } else {
        const viewer = document.createElement('model-viewer');
        // Attributes are set before upgrade on purpose: assigning properties to an
        // unresolved custom element shadows its accessors and drops the URL.
        viewer.setAttribute('src', source.url);
        viewer.setAttribute('alt', this.stage?.getAttribute('aria-label') || 'Etkileşimli 3D ürün görünümü');
        this.modelSlot.append(viewer);
      }
      const viewer = this.modelSlot.querySelector('model-viewer');
      if (!viewer) return null;
      /* model-viewer"in kendi tembel yuklemesi bizimkinden dar bir kapi: mountModel
         yalnizca tiklamadan ya da kendi IntersectionObserver"imizden sonra cagriliyor,
         yani karar zaten verilmis. Ikinci kapi kalirsa model hic istenmiyor (olculdu:
         src dogru, element boyutlu, ama tek bir ag istegi yok). */
      /* orientation modeli KENDI ekseninde dondurur, kamerayi degil — yani salinim
         (cameraOrbit) ve donme siniri ile catismaz, ikisi ayri uzayda. Sirasi
         model-viewer"in bekledigi gibi: roll pitch yaw. */
      const aci = (name) => {
        const v = Number(this.dataset[name]);
        return Number.isFinite(v) ? ((v % 360) + 360) % 360 : 0;
      };
      const roll = aci('modelRoll');
      if (roll) viewer.setAttribute('orientation', `${roll}deg 0deg 0deg`);

      viewer.setAttribute('loading', 'eager');
      viewer.setAttribute('camera-controls', '');
      viewer.setAttribute('interaction-prompt', 'none');
      viewer.setAttribute('shadow-intensity', '1.1');
      viewer.setAttribute('environment-image', 'neutral');
      viewer.setAttribute('exposure', this.classList.contains('is-led-on') ? '1.15' : '.85');

      /* Tekerlek yakalanmasin: camera-controls varsayilan olarak scroll"u
         yakinlastirmaya cevirir ve model uzerindeyken sayfa kaymaz. */
      viewer.setAttribute('disable-zoom', '');

      /* Donme siniri: dinlenme goruntusunden iki eksende de bu kadar cevrilebilir.
         phi kutuplarda bozulmasin diye 2-178 araliginda tutuluyor. */
      const limit = Math.min(90, Math.max(15, Number(this.dataset.orbitLimit) || 90));
      const restTheta = aci('restTheta');
      const restPhi = Math.min(160, Math.max(20, Number(this.dataset.restPhi) || 75));
      this.restTheta = restTheta;
      this.restPhi = restPhi;

      /* Sinirlar DINLENME goruntusune gore, cunku ayarin sozu bu: "musteri
         dinlenme goruntusunden bu kadar cevirebilir". Mutlak alsaydik dinlenme
         acisi 0"dan uzaklastikca bir yon kilitlenirdi. */
      viewer.setAttribute('camera-orbit', `${restTheta}deg ${restPhi}deg auto`);
      viewer.setAttribute('min-camera-orbit', `${restTheta - limit}deg ${Math.max(2, restPhi - limit)}deg auto`);
      viewer.setAttribute('max-camera-orbit', `${restTheta + limit}deg ${Math.min(178, restPhi + limit)}deg auto`);

      /* Salinim ile otomatik donus ayni degeri yazar, birlikte calisamazlar. */
      const swayOn = parseBoolean(this.dataset.scrollSway) && !reducedMotion();
      if (!swayOn && parseBoolean(this.dataset.autoRotate) && !reducedMotion()) viewer.setAttribute('auto-rotate', '');
      if (swayOn) this.startScrollSway(viewer);
      return viewer;

    }

    /* Hero ekrandayken kaydirmayla hafif salinim: saga, sonra sola. Musteri
       modeli kendi surukledigi anda durur — kontrolu ondan geri almiyoruz. */
    startScrollSway(viewer) {
      const amount = Math.min(12, Math.max(2, Number(this.dataset.scrollSwayDegrees) || 5));
      let userTook = false;
      let queued = false;

      /* Yalnizca SURUKLEME kontrolu devralir. camera-change dinlemek yanlisti:
         model uzerindeki tekerlek hareketi de tetikliyor ve salinim ilk
         kaydirmada duruyordu (olculdu: theta 2.44"te donuyordu). */
      viewer.addEventListener('pointerdown', () => {
        userTook = true;
        window.removeEventListener('scroll', onScroll);
      }, { once: true });

      const apply = () => {
        queued = false;
        if (userTook || !viewer.isConnected) return;
        const rect = this.getBoundingClientRect();
        const span = rect.height || 1;
        // 0 = hero ust kenari ekranin ustunde, 1 = tamamen gecilmis
        const progress = Math.min(1, Math.max(0, -rect.top / span));
        const theta = (this.restTheta || 0) + Math.sin(progress * Math.PI * 2) * amount;
        viewer.cameraOrbit = `${theta.toFixed(2)}deg ${this.restPhi || 75}deg auto`;
      };

      const onScroll = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(apply);
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      apply();
    }

    async loadModel(userInitiated) {
      if (this.loading || this.classList.contains('is-model-ready') || !this.modelSlot) return;
      this.loading = true;
      // Download the model while the viewer module loads, rather than after it.
      this.prefetchModel();
      if (this.status) this.status.textContent = this.dataset.loadingLabel || '3D görünüm yükleniyor…';
      this.loadButton?.setAttribute('aria-busy', 'true');

      try {
        await loadModelViewer(this.dataset.modelViewerSrc);
        const viewer = this.mountModel();
        if (!viewer) throw new Error('3D görüntüleyici kullanılamıyor');

        let settled = false;
        const reveal = () => {
          if (settled) return;
          settled = true;
          this.classList.add('is-model-ready');
          if (this.status) this.status.textContent = '';
          if (this.gesture) this.gesture.hidden = false;
          if (this.carousel) {
            this.carousel.hidden = false;
            this.querySelector('[data-orv3d-prev]')?.removeAttribute('hidden');
            this.querySelector('[data-orv3d-next]')?.removeAttribute('hidden');
            this.syncCarousel();
          }
          this.loadButton?.setAttribute('hidden', '');
          this.loadButton?.removeAttribute('aria-busy');
          this.loading = false;
        };
        const fail = () => {
          if (settled) return;
          settled = true;
          /* Model gelmedi: cizilen saat ve "3D incele" dugmesi geri gelsin. */
          this.classList.add('is-model-failed');
          if (this.status) this.status.textContent = this.dataset.unavailableLabel || '3D görünüm şu anda kullanılamıyor.';
          this.loadButton?.removeAttribute('aria-busy');
          this.loading = false;
        };
        viewer.addEventListener('load', reveal, { once: true });
        viewer.addEventListener('error', fail, { once: true });
        if (viewer.loaded) reveal();
        window.setTimeout(fail, 25000);

        if (userInitiated) {
          window.Metasery?.publishEvent?.('metasery:3d_opened', {
            placement: 'homepage_hero',
            section_id: this.dataset.sectionId || ''
          });
        }
      } catch {
        this.classList.add('is-model-failed');
        if (this.status) this.status.textContent = this.dataset.unavailableLabel || '3D görünüm şu anda kullanılamıyor.';
        this.loadButton?.removeAttribute('aria-busy');
        this.loading = false;
      }
    }
  }

  if (!customElements.get('metasery-hero-3d')) customElements.define('metasery-hero-3d', MetaseryHero3d);
})();
